'use client'

/**
 * Purpose: Visually hidden polite live region that announces the completion of async work
 * (product scan, survey scoring, admin match analysis) to screen readers — docs/accessibility.md §8.
 * Inputs: `id` (per docs/element-ids.md) and `message` — already-translated announcement copy.
 * Pass an empty string while work is still pending to keep the region silent.
 * Outputs: A `role="status"` / `aria-live="polite"` <p>. The region mounts empty and receives
 * `message` on a short delay, because assistive tech only reports *changes* inside a live region
 * that already existed — text present at mount time is not announced.
 */
import { useEffect, useState } from 'react'

/** Delay before injecting text so AT registers the (empty) region first. */
const ANNOUNCE_DELAY_MS = 100

type Props = {
  id: string
  message: string
}

export function ResultAnnouncer({ id, message }: Props) {
  // Starts empty on purpose; populated after mount so the mutation is what gets announced.
  const [announced, setAnnounced] = useState('')

  // Re-announce whenever the message changes (e.g. a second scan); an empty message clears the
  // region on the same deferred path. Clearing to '' before a new message is not needed because
  // aria-atomic re-reads the whole region on any change.
  useEffect(() => {
    const timer = setTimeout(() => setAnnounced(message), ANNOUNCE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [message])

  return (
    <p id={id} role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {announced}
    </p>
  )
}
